import type { InsertAttestationConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertAttestationConfigDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * Public input for the `insertAttestationConfig` mutation. Mirrors the legacy
 * `InsertAttestationConfigMutationVariables['object']` (the
 * `InsertAttestationConfigInput`) shape so consumers can forward the same
 * object to either path.
 */
export interface InsertAttestationConfigInput {
  ParentId: string;
  RequireGlobalAttestation: boolean;
  AttestationGroupIds: string[];
  AttestationPromptText?: string | null;
  AttestationTimeLimit?: string | null;
}

/**
 * Maps the tRPC mutation response to match the GraphQL mutation response shape
 * so consumers branching on the result do not need to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  Id: string;
}): InsertAttestationConfigMutation => ({
  insertChildAttestationConfig: { Id: data.Id },
});

export const useInsertAttestationConfig = createMutationHook({
  name: 'insertAttestationConfig',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.attestation.upsertConfig.mutationOptions({ onSuccess }),
  mapInput: (input: InsertAttestationConfigInput) => ({
    ParentId: input.ParentId,
    RequireGlobalAttestation: input.RequireGlobalAttestation,
    AttestationGroupIds: input.AttestationGroupIds,
    AttestationPromptText: input.AttestationPromptText,
    AttestationTimeLimit: input.AttestationTimeLimit,
  }),
  mapResponse: (result: { Id: string }) => mapTrpcResponseToGraphQL(result),
  // The attestations Tab re-derives its view from three read families: the
  // document read (attestation config), the attestation-cycle reads, and the
  // attestation register. Invalidate all three routers so the Tab reflects
  // the upserted config after save — matching the Apollo path, which
  // re-fetched via those reads with no inline cache update.
  invalidate: (trpc) => [
    trpc.frontend.document.pathKey(),
    trpc.frontend.attestationCycle.pathKey(),
    trpc.frontend.attestation.pathKey(),
  ],
  graphqlDocument: InsertAttestationConfigDocument,
  graphqlVariables: (input: InsertAttestationConfigInput) => ({
    object: {
      ParentId: input.ParentId,
      RequireGlobalAttestation: input.RequireGlobalAttestation,
      AttestationGroupIds: input.AttestationGroupIds,
      AttestationPromptText: input.AttestationPromptText,
      AttestationTimeLimit: input.AttestationTimeLimit,
    },
  }),
});
